// ─── Routes ─────────────────────────────────────────────
export const ROUTES = {
  HOME: "/",
  ONBOARDING: "/onboarding",
  ORG_ONBOARDING: "/onboarding/org",
  GET_READY: "/get-ready",
  DASHBOARD: "/dashboard",
  DISCOVER: "/discover",
  BOUNTIES: "/bounties",
  BOUNTY_DETAIL: "/bounty/:id",
  QUEST_DETAIL: "/quest/:id",
  TASK_DETAIL: "/quest/:id/task/:taskId",
  WORK: "/work",
  PROFILE: "/profile",
  PUBLIC_PROFILE: "/u/:handle",
  NOTIFICATIONS: "/notifications",
  ASSISTANT: "/assistant",
  /**
   * An open workspace conversation. The quest id lives in the route rather
   * than in a store, so the conversation survives a reload and can be linked
   * or bookmarked. `useWorkspaceContext` reads it back with `useMatch`.
   */
  ASSISTANT_WORKSPACE: "/assistant/w/:questId",
  /** People-chat inbox; rows open ASSISTANT_WORKSPACE. */
  CHATS: "/chats",
  ADMIN: "/admin",
  /** Public organization page (20261007100000). */
  ORG: "/o/:slug",
  /** Members, invites, requests and settings — org admins. */
  ORG_MANAGE: "/o/:slug/manage",
  ORGS_NEW: "/orgs/new",
  /** Invite-link landing; the code is the credential. */
  ORG_JOIN: "/join/o/:code",
  /** Skill / favor exchange pool (20261025100000). */
  EXCHANGE: "/exchange",
  VOLUNTEER: "/volunteer",
  WORKER: "/worker",
  ORGANIZATIONS: "/organizations",
  PRIVACY: "/privacy",
  TERMS: "/terms",
  /** @deprecated Use ASSISTANT instead */
  CLAW: "/claw",
} as const;

/**
 * Build the route for an open workspace conversation. Derived from the pattern
 * rather than retyping it: `useWorkspaceContext` matches on
 * ROUTES.ASSISTANT_WORKSPACE, so a second hand-written copy of the path could
 * drift from the one that decides whether workspace mode turns on at all.
 * `thread` selects the tab — see lib/workspace/threads.ts.
 */
export function workspaceRoute(questId: string, thread?: string): string {
  const path = ROUTES.ASSISTANT_WORKSPACE.replace(":questId", questId);
  return thread ? `${path}?thread=${encodeURIComponent(thread)}` : path;
}

/** My Work opened on the My Quests tab — where a creator lands after posting. */
export const MY_QUESTS_ROUTE = `${ROUTES.WORK}?tab=quests`;

/** Public Canny board where users submit and vote on feature requests. */
export const FEEDBACK_URL = "https://wexare.canny.io/feature-requests";
export const BLOG_URL = "https://blog.wexare.com";

/**
 * Canonical public legal documents. Registration links here (not to the in-app
 * /terms and /privacy copies) because these are the published versions a new
 * account agrees to; they also have to be absolute for the Privy modal footer.
 */
export const TERMS_OF_SERVICE_URL = "https://www.wexare.com/terms";
export const PRIVACY_POLICY_URL = "https://www.wexare.com/privacy";
/**
 * Effective date of the Terms of Service and Privacy Policy a new sign-in
 * accepts (both documents' "Effective date" line). Bump it when either changes
 * materially: users then record the new version on their next login.
 */
export const TERMS_VERSION = "2026-11-02";

// ─── Animation Tokens ───────────────────────────────────
export const MOTION = {
  spring: { type: "spring" as const, stiffness: 300, damping: 30 },
  springGentle: { type: "spring" as const, stiffness: 200, damping: 25 },
  fadeInUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
  },
  scaleIn: {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
  },
  swipeThreshold: 150,
  swipeVelocity: 500,
} as const;

// ─── Breakpoints ────────────────────────────────────────
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
} as const;

// ─── Quest Categories ───────────────────────────────────
export const QUEST_CATEGORIES = [
  { value: "Development", labelKey: "category.Development" },
  { value: "Design", labelKey: "category.Design" },
  { value: "Writing", labelKey: "category.Writing" },
  { value: "Research", labelKey: "category.Research" },
  { value: "Learning", labelKey: "category.Learning" },
  { value: "Marketing", labelKey: "category.Marketing" },
  { value: "Data", labelKey: "category.Data" },
  { value: "Other", labelKey: "category.Other" },
] as const;

// ─── Admin ──────────────────────────────────────────────
export const ADMIN_STALE_THRESHOLD_DAYS = 7;
export const ADMIN_PAGE_SIZE = 25;

// ─── Limits ─────────────────────────────────────────────
export const LIMITS = {
  QUEST_TITLE_MAX: 120,
  SKILL_MIN: 1,
  SKILL_MAX: 15,
  QUEST_BUDGET_MAX: 100_000,
  FILE_MAX_SIZE_MB: 10,
  FILE_MAX_COUNT: 5,
  TOAST_DURATION_MS: 4000,
  /** For toasts the reader must act on, not just acknowledge. */
  TOAST_LONG_DURATION_MS: 14_000,
  DISCOVER_BATCH_SIZE: 20,
  DISCOVER_PREFETCH_THRESHOLD: 5,
} as const;
